import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CategorySidebar, type SidebarSection } from '@/common/CategorySidebar';
import { EmptySearchState } from '@/common/EmptySearchState';
import { LoadMoreTrigger } from '@/common/LoadMoreButton';
import { CategoryDrawer } from '@/common/mobile/CategoryDrawer';
import { WBSButton } from '@/common/WBSButton';
import {
  ArticleGrid,
  ArticlePageContainer,
  MainContent,
} from '~/styles/articlePageStyles';

interface ArticleListingPageProps<T> {
  items: T[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  sidebarData: SidebarSection[];
  showSearch?: boolean;
  renderCard: (item: T, index: number) => ReactNode;
  searchTerm?: string;
  onSearch?: (e: React.FormEvent<HTMLFormElement>) => void;
}

export function ArticleListingPage<T>({
  items,
  isLoading,
  hasMore,
  onLoadMore,
  sidebarData,
  showSearch = false,
  renderCard,
  searchTerm,
  onSearch,
}: ArticleListingPageProps<T>) {
  const { t } = useTranslation();
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);

  return (
    <ArticlePageContainer>
      <MainContent>
        <MobileTriggerContainer>
          <WBSButton wide onClick={() => setIsCategoriesOpen(true)}>
            {t('wbs.categories-2')}
          </WBSButton>
        </MobileTriggerContainer>

        {/*
          `hasMore` gates this too: the tutorials search filters the
          already-loaded pages client-side, so an early page can legitimately
          contain no matches while a later one does. Claiming "nothing found"
          before the feed is fully loaded would be wrong.
        */}
        {!isLoading && !hasMore && items.length === 0 ? (
          <EmptySearchState
            // Same catalogue key the follow-up-tutorials archive uses
            // (`wbs.it-seems-we-can`, WPML unit 3062, present in all four
            // locales). Identical English, so the two Wave-5 routes that mount
            // this component render exactly what they rendered before; es/fr/
            // pt-br stop falling back to English.
            subtitle={t('wbs.it-seems-we-can')}
            searchTerm={searchTerm}
            onSearch={onSearch}
          />
        ) : (
          <ArticleGrid>
            {items.map((item, index) => renderCard(item, index))}
          </ArticleGrid>
        )}

        <LoadMoreTrigger
          onLoadMore={onLoadMore}
          isLoading={isLoading}
          hasMore={hasMore}
        />
      </MainContent>

      <DesktopSidebar>
        <CategorySidebar
          sidebarData={sidebarData}
          showSearch={showSearch}
          searchTerm={searchTerm}
          onSearch={onSearch}
        />
      </DesktopSidebar>
      <CategoryDrawer
        isOpen={isCategoriesOpen}
        onClose={() => setIsCategoriesOpen(false)}
        drawerData={sidebarData}
        showSearch={showSearch}
        searchTerm={searchTerm}
        onSearch={onSearch}
      />
    </ArticlePageContainer>
  );
}

const MobileTriggerContainer = styled.div`
  display: none;
  margin-bottom: 20px;

  @media (max-width: 799px) {
    display: block;
  }
`;

const DesktopSidebar = styled.div`
  @media (max-width: 799px) {
    display: none;
  }
`;
